<template>
    <div class="grafico-container grafico-destaque">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Índice de Performance de Prazo (IPP)</h3>
            <GraficoInfoIcon 
                titulo="Índice de Performance de Prazo (IPP)"
                descricao="O IPP mede a eficiência dos projetos em relação ao prazo. Calculado pela relação entre o percentual de tarefas concluídas e o tempo decorrido em relação ao prazo total do projeto. Valores próximos ou acima de 1 indicam boa performance. Valores negativos indicam projetos muito atrasados (mais de 5x o prazo). O gráfico mostra todos os projetos válidos, destacando os melhores e piores desempenhos."
                formula="IPP = (Tarefas Concluídas / Total de Tarefas) / (Tempo Decorrido / Prazo Total)"
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoIPP',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        temDados() {
            return this.projetos.some(p => this.calcularIPP(p) !== null && this.calcularIPP(p) !== undefined);
        },
        projetosComIPP() {
            const projetosCalculados = this.projetos
                .map(p => ({
                    projeto: p,
                    ipp: this.calcularIPP(p)
                }))
                .filter(item => item.ipp !== null && item.ipp !== undefined);
            
            if (projetosCalculados.length === 0) return [];
            
            const ordenados = projetosCalculados.sort((a, b) => b.ipp - a.ipp);
            
            if (ordenados.length <= 50) {
                return ordenados;
            }
            
            const topMelhores = ordenados.slice(0, 25);
            const topPiores = ordenados.slice(-25);
            
            const combinados = [...topMelhores, ...topPiores];
            const unicos = combinados.filter((item, index, self) => 
                index === self.findIndex(t => t.projeto.id === item.projeto.id)
            );
            
            return unicos.sort((a, b) => b.ipp - a.ipp);
        },
        chartData() {
            const projetos = this.projetosComIPP;
            
            return {
                labels: projetos.map(item => this.formatarNomeProjeto(item.projeto)),
                datasets: [{
                    label: 'IPP',
                    data: projetos.map(item => item.ipp),
                    backgroundColor: projetos.map(item => {
                        if (item.ipp >= 1.0) return 'rgba(46, 204, 113, 0.6)';
                        if (item.ipp >= 0.8) return 'rgba(52, 152, 219, 0.6)';
                        if (item.ipp >= 0.5) return 'rgba(241, 196, 15, 0.6)';
                        if (item.ipp >= 0) return 'rgba(231, 76, 60, 0.6)';
                        return 'rgba(192, 57, 43, 0.6)';
                    }),
                    borderColor: projetos.map(item => {
                        if (item.ipp >= 1.0) return 'rgba(46, 204, 113, 1)';
                        if (item.ipp >= 0.8) return 'rgba(52, 152, 219, 1)';
                        if (item.ipp >= 0.5) return 'rgba(241, 196, 15, 1)';
                        if (item.ipp >= 0) return 'rgba(231, 76, 60, 1)';
                        return 'rgba(192, 57, 43, 1)';
                    }),
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            const projetosComIPP = this.projetosComIPP;
            
            return {
                responsive: true,
                maintainAspectRatio: false,
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const item = projetosComIPP[index];
                        if (item) {
                            self.$emit('clicar-projeto', item.projeto.id);
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: false,
                        title: {
                            display: true,
                            text: 'IPP'
                        },
                        ticks: {
                            callback: (value) => value.toFixed(2)
                        },
                        grid: {
                            color: (context) => {
                                if (context.tick.value === 0) {
                                    return 'rgba(0, 0, 0, 0.5)';
                                }
                                return 'rgba(0, 0, 0, 0.1)';
                            }
                        }
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const item = projetosComIPP[context.dataIndex];
                                const porcentagem = item.projeto.estatisticas?.tarefas?.porcentagem_concluidas ?? 0;
                                
                                // Usar apenas dados do PCM (obrigatórios para IPP)
                                const dtInicio = item.projeto.pcm?.dtInicio;
                                const dtEstimadaFinal = item.projeto.pcm?.dtEstimadaFinal;
                                
                                let percentualTempo = 0;
                                let diasDecorridos = 0;
                                let diasTotal = 0;
                                
                                if (dtInicio && dtEstimadaFinal) {
                                    const hoje = new Date();
                                    hoje.setHours(0, 0, 0, 0);
                                    const inicio = new Date(dtInicio);
                                    inicio.setHours(0, 0, 0, 0);
                                    const termino = new Date(dtEstimadaFinal);
                                    termino.setHours(23, 59, 59, 999);
                                    
                                    if (!isNaN(inicio.getTime()) && !isNaN(termino.getTime())) {
                                        diasTotal = Math.max(1, Math.ceil((termino - inicio) / (1000 * 60 * 60 * 24)));
                                        const dataReferencia = hoje > termino ? termino : hoje;
                                        diasDecorridos = Math.max(0, Math.ceil((dataReferencia - inicio) / (1000 * 60 * 60 * 24)));
                                        
                                        if (diasTotal > 0) {
                                            const percentualCalculado = (diasDecorridos / diasTotal) * 100;
                                            percentualTempo = Math.min(1000, percentualCalculado);
                                        }
                                    }
                                }
                                
                                let tempoTexto = '';
                                if (diasDecorridos === 0) {
                                    tempoTexto = 'Tempo: 0.0% decorrido (projeto ainda não iniciou)';
                                } else if (percentualTempo > 1000) {
                                    tempoTexto = 'Tempo: >1000% decorrido (projeto muito atrasado)';
                                } else {
                                    tempoTexto = `Tempo: ${percentualTempo.toFixed(1)}% decorrido`;
                                }
                                
                                return [
                                    `IPP: ${context.parsed.y.toFixed(2)}`,
                                    `Tarefas: ${porcentagem.toFixed(1)}% concluídas`,
                                    tempoTexto
                                ];
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    },
    methods: {
        formatarNomeProjeto(projeto, maxLength = 25) {
            const nome = projeto.nome || 'Sem nome';
            const codigoPCM = projeto.pcm?.codigo || '';
            const nomeCompleto = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
            return nomeCompleto.length > maxLength ? nomeCompleto.substring(0, maxLength) + '...' : nomeCompleto;
        },
        calcularIPP(projeto) {
            // IPP requer PCM com dtInicio E dtEstimadaFinal
            if (!projeto.pcm || !projeto.pcm.dtInicio || !projeto.pcm.dtEstimadaFinal) {
                return null;
            }
            
            const porcentagemConcluidas = projeto.estatisticas?.tarefas?.porcentagem_concluidas ?? 0;
            const totalTarefas = projeto.estatisticas?.tarefas?.total ?? 0;
            const percentualConcluido = porcentagemConcluidas / 100;
            
            // Usar datas do PCM (obrigatórias)
            const dtInicio = projeto.pcm.dtInicio;
            const dtEstimadaFinal = projeto.pcm.dtEstimadaFinal;
            
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            let inicio = null;
            let termino = null;
            let diasTotal = 1;
            let diasDecorridos = 0;
            
            if (dtInicio) {
                inicio = new Date(dtInicio);
                inicio.setHours(0, 0, 0, 0);
                if (isNaN(inicio.getTime())) {
                    inicio = null;
                }
            }
            
            if (dtEstimadaFinal) {
                termino = new Date(dtEstimadaFinal);
                termino.setHours(23, 59, 59, 999);
                if (isNaN(termino.getTime())) {
                    termino = null;
                }
            }
            
            // Como dtInicio e dtEstimadaFinal são obrigatórios, sempre teremos ambos
            if (inicio && termino) {
                diasTotal = Math.max(1, Math.ceil((termino - inicio) / (1000 * 60 * 60 * 24)));
                const dataReferencia = hoje > termino ? termino : hoje;
                diasDecorridos = Math.max(0, Math.ceil((dataReferencia - inicio) / (1000 * 60 * 60 * 24)));
            } else {
                // Se as datas não são válidas, retornar null
                return null;
            }
            
            const percentualTempo = diasTotal > 0 ? (diasDecorridos / diasTotal) : 0.01;
            
            if (diasDecorridos === 0 || percentualTempo < 0.01) {
                if (totalTarefas === 0) return 0;
                if (porcentagemConcluidas === 0) return 0;
                return Math.min(1, percentualConcluido);
            }
            
            if (totalTarefas === 0) {
                if (percentualTempo > 1) return -0.5;
                return 0;
            }
            
            if (porcentagemConcluidas === 0) {
                if (percentualTempo > 1) {
                    return -Math.min(5, percentualTempo - 1);
                }
                return 0;
            }
            
            if (percentualTempo === 0 || percentualTempo < 0.01) {
                return porcentagemConcluidas > 0 ? Math.min(1, percentualConcluido) : 0;
            }
            
            let ipp = percentualConcluido / percentualTempo;
            
            if (percentualTempo > 5) {
                ipp = -Math.min(10, (percentualTempo - 1) / 2);
            }
            
            const ippLimitado = Math.max(-10, Math.min(10, ipp));
            
            return Math.round(ippLimitado * 100) / 100;
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 800px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-destaque {
    border: 2px solid var(--cor-primaria, #3697d3);
    box-shadow: 0 4px 12px rgba(54, 151, 211, 0.2);
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

